import {
  booleanAttribute,
  computed,
  DestroyRef,
  Directive,
  inject,
  input,
  signal,
} from '@angular/core';
import type { ValidationErrors, Validator } from '@angular/forms';
import { AbstractWithDelegatedFormControlComponent } from '@pct-ng/form';
import type { Icons } from '@pct/icon';
import {
  type AbstractFileUploadService,
  isFileUploadResponseEqual,
  type ErrorFile,
  FILE_UPLOAD_SERVICE_TOKEN,
  type FileUploadConfig,
  type FileUploadResponse,
  isErrorFile,
  isErrorFileUploadResponse,
  isSuccessFileUploadResponse,
  type NotSavedFileUploadResponse,
  type SelectedFile,
} from '@pct-ng/feature-file-upload-native';
import { handleHttpError } from '@pct-ng/core/error-http';
import type { Observable } from 'rxjs';
import {
  BehaviorSubject,
  filter,
  finalize,
  map,
  from,
  concatMap,
  toArray,
  switchMap,
  of,
} from 'rxjs';
import { outputFromObservable, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { deepDistinctUntilChanged } from '@pct/rxjs';
import { isNil, isString } from '@pct-ts/typeguard';
import { I18nProviderInjectionToken } from './i18n/scope';
import { TranslocoService } from '@jsverse/transloco';
import { assertIsNotNil } from '@pct-ts/assert';
import { DialogService } from '@cappus/shared-common';
import { ImageCropDialogComponent } from './image-crop-dialog/image-crop-dialog.component';
import {
  openConfirmDialog,
  openOKDialog,
  type MessageResult,
} from '@pct-ng/feature-message-material';

interface FilesSelect<T> {
  files: FileUploadResponse<T>[];
  change: boolean;
}

function createFilesSelect<T>(files: FileUploadResponse<T>[], change = true): FilesSelect<T> {
  return {
    files,
    change,
  };
}

@Directive({})
export abstract class AbstractMaterialFileUploadComponent<T>
  extends AbstractWithDelegatedFormControlComponent<T | T[]>
  implements Validator
{
  readonly #destroyRef = inject(DestroyRef);
  readonly #translocoService = inject(TranslocoService);
  readonly #fileUploadService: AbstractFileUploadService<T> | null = inject(
    FILE_UPLOAD_SERVICE_TOKEN,
    { optional: true },
  );
  readonly #i18nScope: string = inject(I18nProviderInjectionToken);

  readonly i18nScope = input<string>();
  readonly hideCardShadow = input(false, { transform: booleanAttribute });
  readonly service = input<AbstractFileUploadService<T> | null>();
  readonly config = input.required<FileUploadConfig>();
  readonly centerIcon = input<Icons>('pct-mdi-cloud-upload-outline');
  readonly centerDragOverIcon = input<Icons>('pct-mdi-cloud-upload');

  readonly #filesSelect = new BehaviorSubject<FilesSelect<T>>(createFilesSelect([], false));
  readonly filesSelect = outputFromObservable(this.#filesSelect.pipe(map(({ files }) => files)));
  readonly errors = outputFromObservable(
    this.#filesSelect
      .pipe(map(files => files.files.filter(isErrorFileUploadResponse)))
      .pipe(deepDistinctUntilChanged()),
  );

  protected readonly loading = signal<boolean>(false);
  protected readonly uploading = signal<boolean>(false);
  protected readonly dragOver = signal<boolean>(false);
  protected readonly notSavedFileList = signal<NotSavedFileUploadResponse[] | null>(null);
  protected readonly selectedFiles = toSignal(this.#filesSelect, { requireSync: true });
  protected readonly effectiveDisabled = computed(() => this.isDisabled() || this.loading());
  protected readonly effectiveErrors = computed(() =>
    this.selectedFiles().files.filter(isErrorFileUploadResponse),
  );
  protected readonly effectiveSelectedFiles = computed(() => {
    const notSaveFileList = this.notSavedFileList();
    if (notSaveFileList !== null && notSaveFileList.length > 0) {
      return createFilesSelect(notSaveFileList, false);
    }
    return this.selectedFiles() ?? createFilesSelect([], false);
  });
  protected readonly effectiveService = computed(() => {
    const service = this.service() ?? this.#fileUploadService;
    assertIsNotNil(service, 'Not defined file upload service');
    return service;
  });
  protected readonly effectiveI18nScope = computed(
    (): string => this.i18nScope() ?? this.#i18nScope,
  );

  protected readonly dialogService = inject(DialogService);

  constructor() {
    super();

    this.#filesSelect
      .pipe(
        filter(({ change }) => change),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(files => {
        const ids = files.files.filter(isSuccessFileUploadResponse<T>).map(file => file.id);
        this.changeValue(this.config().multiple ? ids : ids[0]);
      });
  }

  writeValue(value: T | T[]): void {
    if (Array.isArray(value)) {
      if (value.length === 0) {
        this.#filesSelect.next(createFilesSelect([], false));
        return;
      }
      this.loading.set(true);
      this.effectiveService()
        .getByIds(value)
        .pipe(
          finalize(() => this.loading.set(false)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe({
          next: response => this.#filesSelect.next(createFilesSelect(response, false)),
          error: handleHttpError(() => this.#filesSelect.next(createFilesSelect([], false))),
        });
    } else if (!isNil(value) && (!isString(value) || (isString(value) && value.length > 0))) {
      this.loading.set(true);
      this.effectiveService()
        .getById(value)
        .pipe(
          finalize(() => this.loading.set(false)),
          takeUntilDestroyed(this.#destroyRef),
        )
        .subscribe({
          next: response => this.#filesSelect.next(createFilesSelect([response], false)),
          error: handleHttpError(() => this.#filesSelect.next(createFilesSelect([], false))),
        });
    } else {
      this.#filesSelect.next(createFilesSelect([], false));
    }
  }

  validate(): ValidationErrors | null {
    if (this.effectiveErrors().length > 0) {
      return {
        fileUploadError: true,
      };
    }

    return null;
  }

  protected onFileSelect(fileResults: SelectedFile[]): void {
    this.onTouched();
    this.loading.set(true);
    this.uploading.set(true);

    const validFiles = fileResults.filter(result => result.isValid).map(result => result.file);
    const invalidFiles = fileResults.filter(isErrorFile);

    // Handle invalid files
    if (invalidFiles.length > 0) {
      if (this.config().multiple) {
        // Multiple mode: show dialog with list of invalid files, then proceed with valid files
        this.#showMultipleModeInvalidFilesDialog(invalidFiles).subscribe(() => {
          this.#processValidFiles(validFiles);
        });
      } else {
        // Single mode: show dialog for the invalid file and stop processing
        this.#showSingleModeInvalidFileDialog(invalidFiles[0]).subscribe(() => {
          this.loading.set(false);
          this.uploading.set(false);
        });
        return;
      }
    } else {
      // No invalid files, proceed with valid files
      this.#processValidFiles(validFiles);
    }
  }

  #processValidFiles(validFiles: File[]): void {
    const config = this.config();
    const overwriteNewUpload = config.overwriteNewUpload ?? true;
    this.notSavedFileList.set(validFiles.map(file => ({ file })));

    if (validFiles.length === 0) {
      this.notSavedFileList.set(null);
      this.loading.set(false);
      this.uploading.set(false);
      if (overwriteNewUpload) {
        return this.#filesSelect.next(createFilesSelect([]));
      }
      return;
    }
    if (isNil(config.cropConfig)) {
      this.#uploadFiles(validFiles).subscribe();
    } else {
      this.#processValidFilesWithCrop(validFiles);
    }
  }

  #processValidFilesWithCrop(validFiles: File[]): void {
    // Iterálunk végig az összes valid fájlon és egyesével megnyitjuk a crop dialógust
    from(validFiles)
      .pipe(
        concatMap(file =>
          this.dialogService
            .openFromComponent(
              ImageCropDialogComponent,
              {
                selectedFile: file,
                cropConfig: this.config().cropConfig!,
              },
              {
                width: '80%',
                height: '80%',
              },
            )
            .afterClosed()
            .pipe(
              map(result => ({
                originalFile: file,
                croppedFile: result,
              })),
            ),
        ),
        map(({ croppedFile }) => croppedFile), // Csak a croppolt fájlokat tartjuk meg
        filter(croppedFile => !isNil(croppedFile)), // Kiszűrjük azokat, ahol a user bezárta a dialógust crop nélkül
        // Összegyűjtjük az összes croppolt fájlt egy tömbbe
        toArray(),
        switchMap((croppedFiles: File[]) => {
          if (croppedFiles.length === 0) {
            this.notSavedFileList.set(null);
            this.loading.set(false);
            this.uploading.set(false);
            return of([]);
          }
          return this.#uploadFiles(croppedFiles);
        }),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe({
        next: () => {
          // Feltöltés sikeres
        },
        error: handleHttpError(() => {
          this.loading.set(false);
          this.uploading.set(false);
        }),
      });
  }

  #uploadFiles(files: File[]): Observable<FileUploadResponse<T>[]> {
    const config = this.config();
    const overwriteNewUpload = config.overwriteNewUpload ?? true;
    this.notSavedFileList.set(files.map(file => ({ file })));

    this.delegatedControl.markAsPending({ emitEvent: false });

    const after = (response: FileUploadResponse<T>[]): void => {
      const validResponse = response.filter(
        responseFile => !isErrorFileUploadResponse(responseFile),
      );
      const newFiles = {
        change: true,
        files: [
          ...(overwriteNewUpload ? [] : this.#filesSelect.getValue().files),
          ...createFilesSelect(validResponse).files,
        ],
      } as FilesSelect<T>;
      this.#filesSelect.next(newFiles);
      this.notSavedFileList.set(null);
      this.delegatedControl.updateValueAndValidity({ emitEvent: false });
    };

    const service = this.effectiveService();
    if (config.multiple) {
      return service.uploadFiles(files).pipe(
        finalize(() => {
          this.loading.set(false);
          this.uploading.set(false);
        }),
        map(responses => {
          after(responses);
          return responses;
        }),
      );
    }
    return service.uploadFile(files[0]).pipe(
      finalize(() => {
        this.loading.set(false);
        this.uploading.set(false);
      }),
      map(response => {
        after([response]);
        return [response];
      }),
    );
  }

  protected onDragOver($event: boolean): void {
    this.dragOver.set($event);
  }

  protected onRemove(file: FileUploadResponse<T>, $event?: MouseEvent): void {
    if (!isNil($event)) {
      $event.preventDefault();
      $event.stopPropagation();
    }

    const after = (response?: any): void => {
      if (response === null) {
        return;
      }
      this.#filesSelect.next(
        createFilesSelect(
          this.#filesSelect.getValue().files.filter(f => !isFileUploadResponseEqual(file, f)),
        ),
      );
      this.notSavedFileList.update(
        notSaveFileList =>
          notSaveFileList?.filter(f => !isFileUploadResponseEqual(file, f)) ?? null,
      );
    };
    if (isSuccessFileUploadResponse<T>(file)) {
      openConfirmDialog({
        title: 'COMMON.DELETE_CONFIRM_DIALOG.TITLE',
        message: 'COMMON.DELETE_CONFIRM_DIALOG.MESSAGE',
      })
        .pipe(
          switchMap(messageResult => {
            if (messageResult?.result === 'SUBMIT') {
              return this.effectiveService()
                .remove(file.id)
                .pipe(
                  takeUntilDestroyed(this.#destroyRef),
                  finalize(() => {
                    this.loading.set(false);
                    this.uploading.set(false);
                  }),
                );
            }
            // null = signal
            return of(null);
          }),
        )

        .subscribe({
          next: after.bind(this),
          error: handleHttpError(),
        });
    } else {
      after();
    }
  }

  #showSingleModeInvalidFileDialog(invalidFile: ErrorFile): Observable<MessageResult | undefined> {
    const i18nScope = this.effectiveI18nScope();

    const errorKey = `ERROR.INVALID_FILE.SINGLE_MODE.${invalidFile.errorType}`;
    return openOKDialog({
      title: `${i18nScope}.ERROR.INVALID_FILE.SINGLE_MODE.TITLE`,
      message: `${i18nScope}.${errorKey}`,
    });
  }

  #showMultipleModeInvalidFilesDialog(
    invalidFiles: ErrorFile[],
  ): Observable<MessageResult | undefined> {
    const i18nScope = this.effectiveI18nScope();

    const invalidFilesList = invalidFiles
      .map(file => {
        const errorKey = `ERROR.INVALID_FILE.MULTIPLE_MODE.${file.errorType}`;
        return `• ${file.file.name} - ${this.#translocoService.translate(`${i18nScope}.${errorKey}`)}`;
      })
      .join('\n');

    const message = `${i18nScope}.ERROR.INVALID_FILE.MULTIPLE_MODE.MESSAGE`;

    return openOKDialog({
      title: `${i18nScope}.ERROR.INVALID_FILE.MULTIPLE_MODE.TITLE`,
      message: {
        text: message,
        translate: true,
        translateParams: { list: invalidFilesList },
      },
    });
  }
}
